import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

/**
 * A dropdown you type into.
 *
 * <p>The masters made every one of these lists too long to scroll: thirty-six states, a
 * few hundred cities, nineteen subjects and counting. A native `select` gives you one
 * character of type-ahead and no way to see what matched, so this is a combobox — an
 * input that filters a listbox, with the keyboard behaviour people expect from one
 * (arrows move, Enter takes, Escape closes and puts the text back).
 *
 * <p>Two sources of options, because two kinds of list exist. A short list is passed in
 * whole as `options` and filtered here; a long one is passed as `loadOptions`, a function
 * of the query, and filtered by the server — the city master is thousands of rows, and
 * shipping the whole country to find one town is not a plan.
 *
 * <p>`multiple` turns the same control into a chip list, which is what the teacher's
 * subject field needs. The single and multiple forms share every keystroke rule, so the
 * two cannot drift apart.
 */

/** An option, however it was supplied, as `{ id, name, hint }`. */
function normalize(option) {
  if (option == null) return null;
  if (typeof option === 'string') return { id: option, name: option, hint: null };
  return { id: option.id, name: option.name, hint: option.hint ?? null };
}

/**
 * Whether `text` should be treated as a query rather than as the label of what is
 * already chosen.
 *
 * <p>Pure and exported because the mistake it prevents is a silent one: if the field
 * re-searched for the name it just filled in, choosing an option would immediately
 * reopen the list on the answer the user had just given.
 */
export function isQuery(text, chosenName) {
  return String(text ?? '').trim() !== String(chosenName ?? '').trim();
}

/** Substring match, the same rule the server's `like` applies. */
export function filterOptions(options, query) {
  const needle = String(query ?? '').trim().toLowerCase();
  if (!needle) return options;
  return options.filter((option) => option.name.toLowerCase().includes(needle));
}

export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  loadOptions,
  placeholder = 'Type to search',
  emptyLabel = 'No matches',
  disabled = false,
  allowClear = true,
  multiple = false,
  maxItems,
  error,
  describedBy,
  // For a filter in a toolbar, which has no <Field> label above it to point at.
  ariaLabel,
}) {
  const selected = useMemo(
    () => (multiple ? (value ?? []).map(normalize).filter(Boolean) : []),
    [multiple, value],
  );
  const single = multiple ? null : normalize(value);
  const chosenName = single?.name ?? '';

  const [text, setText] = useState(chosenName);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // A selection made elsewhere — a form reset, an edit modal opening on a record —
  // has to show in the field. Keyed on the id so typing is never overwritten.
  useEffect(() => {
    if (!multiple) setText(chosenName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single?.id, multiple]);

  const query = multiple || isQuery(text, chosenName) ? text.trim() : '';

  useEffect(() => {
    if (!open || !loadOptions) return undefined;
    let cancelled = false;
    setLoading(true);
    // Debounced: a fast typist sends one request, not one per keystroke.
    const handle = setTimeout(() => {
      Promise.resolve(loadOptions(query))
        .then((found) => {
          if (cancelled) return;
          setLoaded((found ?? []).map(normalize).filter(Boolean));
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          // Not swallowed silently: an empty list and a failed lookup look identical
          // otherwise, and the second one is worth saying out loud.
          setLoaded([]);
          setLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, query, loadOptions]);

  const available = useMemo(() => {
    const pool = loadOptions ? loaded : filterOptions((options ?? []).map(normalize).filter(Boolean), query);
    if (!multiple) return pool;
    // Already-chosen chips are not offered again; picking one twice is not a thing.
    const taken = new Set(selected.map((item) => item.id));
    return pool.filter((option) => !taken.has(option.id));
  }, [loadOptions, loaded, options, query, multiple, selected]);

  useEffect(() => setActive(0), [available.length, query]);

  // Closing on an outside click is what makes this a dropdown rather than a panel that
  // will not go away.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!boxRef.current?.contains(event.target)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chosenName]);

  const close = useCallback(() => {
    setOpen(false);
    // Abandoned typing reverts to what is actually selected, so the field never shows
    // a name that was not chosen.
    if (!multiple) setText(chosenName);
  }, [multiple, chosenName]);

  function choose(option) {
    if (multiple) {
      if (maxItems && selected.length >= maxItems) return;
      onChange([...selected, option]);
      setText('');
      inputRef.current?.focus();
      return;
    }
    onChange(option);
    setText(option.name);
    setOpen(false);
  }

  function removeAt(index) {
    onChange(selected.filter((_, position) => position !== index));
  }

  function onKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (available.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + available.length) % available.length);
      return;
    }
    if (event.key === 'Enter') {
      if (!open || available.length === 0) return;
      // Only swallowed when there is something to take, so Enter still submits the
      // form when the list is closed or empty.
      event.preventDefault();
      choose(available[active]);
      return;
    }
    if (event.key === 'Escape') {
      if (open) event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Backspace' && multiple && text === '' && selected.length > 0) {
      removeAt(selected.length - 1);
    }
  }

  const listboxId = `${id}-listbox`;
  const showClear = allowClear && !disabled && (multiple ? selected.length > 0 : Boolean(single?.id));

  return (
    <div className="picker" ref={boxRef}>
      {multiple && selected.length > 0 && (
        <div className="picker-chips">
          {selected.map((item, index) => (
            <span className="picker-chip" key={item.id}>
              {item.name}
              {!disabled && (
                <button
                  type="button"
                  className="picker-chip-x"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => removeAt(index)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="picker-input">
        <input
          id={id}
          name={id}
          ref={inputRef}
          className="input"
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder={multiple && selected.length > 0 ? 'Add another' : placeholder}
          value={text}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
            // Typing past a selection abandons it, which is what lets the next
            // keystroke search again rather than matching the filled-in name.
            if (!multiple && single?.id) onChange(null);
          }}
        />
        {showClear ? (
          <button
            type="button"
            className="picker-clear"
            aria-label="Clear selection"
            onClick={() => {
              onChange(multiple ? [] : null);
              setText('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : (
          // Now that every dropdown in the product is one of these, the field has to
          // look like a dropdown: without this it is a text box, and nobody clicks a
          // text box expecting a list. It shares the slot with the clear button
          // because the two are never both useful — one appears exactly when the
          // other has something to clear.
          <span className="picker-caret" aria-hidden="true">
            <Icon name="chevron" size={16} />
          </span>
        )}
      </div>

      {open && (
        <ul className="suggestions" id={listboxId} role="listbox">
          {available.map((option, index) => (
            <li key={option.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={index === active ? 'is-active' : undefined}
                // Keeps focus in the input, so the blur does not close the list before
                // the click lands.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(option)}
              >
                {option.name}
                {option.hint && <span className="tiny muted"> · {option.hint}</span>}
              </button>
            </li>
          ))}
          {available.length === 0 && (
            <li className="picker-empty">{loading ? 'Searching…' : emptyLabel}</li>
          )}
        </ul>
      )}
    </div>
  );
}
